import * as React from 'react'
import { useChartT } from '../i18n'
import { createPortal } from 'react-dom'

// Lightweight server-search-driven select. The query is the controlled search
// string; results render as a dropdown. Used for both Driver and Vehicle pickers.
type Item = { id: number; label: string }

type Props = {
  menuZIndex?: number
  label: string
  placeholder?: string
  value: number | null
  onChange: (id: number | null) => void
  // The hook returns paginated server results filtered by `search`. Caller passes
  // back a normalized [{ id, label }] list so this component stays generic.
  useSearch: (search: string) => { items: Item[]; isLoading: boolean }
  // Display label when value !== null but the current items don't include it
  // (e.g. just-loaded disc with a saved driverId we haven't searched for yet).
  displayLabel?: string | null
  rightSlot?: React.ReactNode
}

// Where the floating menu should sit, computed from the input's viewport rect.
type MenuPos = { left: number; top: number; width: number; openUp: boolean }

const MENU_MAX_HEIGHT = 240 // matches the max-h-60 we render with

export function SearchSelect({
  menuZIndex = 10000,
  label,
  placeholder,
  value,
  onChange,
  useSearch,
  displayLabel,
  rightSlot,
}: Props) {
  const T = useChartT()
  const [search, setSearch] = React.useState('')
  const [open, setOpen] = React.useState(false)
  const [pos, setPos] = React.useState<MenuPos | null>(null)
  const { items, isLoading } = useSearch(search)
  const fieldRef = React.useRef<HTMLDivElement>(null)
  const menuRef = React.useRef<HTMLDivElement>(null)

  // The menu is portaled to <body>, so it lives outside this component's DOM
  // subtree. Outside-click detection therefore has to spare BOTH the input
  // field and the floating menu.
  React.useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      const t = e.target as Node
      if (fieldRef.current?.contains(t) || menuRef.current?.contains(t)) return
      setOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [])

  // Position the floating menu against the input. Recomputed on open and while
  // open on any scroll/resize so it tracks the field (the field can sit inside
  // a scrollable modal/table). Fixed positioning + body portal means it can't
  // be clipped by the table's overflow or the modal's `overflow: auto`.
  React.useEffect(() => {
    if (!open) return undefined
    const compute = () => {
      const el = fieldRef.current
      if (!el) return
      const r = el.getBoundingClientRect()
      const spaceBelow = window.innerHeight - r.bottom
      const openUp = spaceBelow < MENU_MAX_HEIGHT && r.top > spaceBelow
      setPos({
        left: r.left,
        top: openUp ? r.top : r.bottom,
        width: r.width,
        openUp,
      })
    }
    compute()
    window.addEventListener('scroll', compute, true)
    window.addEventListener('resize', compute)
    return () => {
      window.removeEventListener('scroll', compute, true)
      window.removeEventListener('resize', compute)
    }
  }, [open])

  const selected = items.find((i) => i.id === value)
  const shown = selected?.label ?? displayLabel ?? ''
  const clearable = value != null

  return (
    <div className="tw-relative">
      {label && (
        <label className="tw-mb-1 tw-block tw-text-sm tw-font-medium tw-text-gray-700">
          {label}
        </label>
      )}
      <div className="tw-flex tw-items-center tw-gap-1">
        {/* Input wrapper is the positioning context for the clear button so the
            "×" sits INSIDE the field (matching DateField's delete icon) instead
            of overflowing the cell as a separate flex item. */}
        <div ref={fieldRef} className="tw-relative tw-flex-1">
          <input
            type="text"
            value={open ? search : shown}
            placeholder={placeholder}
            onFocus={() => {
              setSearch('')
              setOpen(true)
            }}
            onChange={(e) => setSearch(e.target.value)}
            className={`tw-block tw-w-full tw-rounded-md tw-border tw-border-gray-300 tw-bg-white tw-py-2 tw-pl-3 tw-text-sm tw-leading-tight tw-placeholder-gray-400 tw-shadow-sm focus:tw-border-blue-500 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500 ${
              clearable ? 'tw-pr-8' : 'tw-pr-3'
            }`}
          />
          {clearable && (
            <button
              type="button"
              onClick={() => onChange(null)}
              className="tw-absolute tw-inset-y-0 tw-right-0 tw-flex tw-items-center tw-justify-center tw-px-2 tw-text-gray-400 hover:tw-text-gray-600"
              title={T('clear', 'Wyczyść')}
            >
              <span className="tw-text-base tw-leading-none">×</span>
            </button>
          )}
        </div>
        {rightSlot}
      </div>
      {open &&
        pos &&
        createPortal(
          <div
            ref={menuRef}
            style={{
              position: 'fixed',
              left: pos.left,
              top: pos.openUp ? undefined : pos.top + 4,
              bottom: pos.openUp ? window.innerHeight - pos.top + 4 : undefined,
              width: pos.width,
              maxHeight: MENU_MAX_HEIGHT,
              zIndex: menuZIndex,
            }}
            className="tw-overflow-auto tw-rounded-md tw-border tw-border-gray-200 tw-bg-white tw-shadow-lg"
          >
            {isLoading && (
              <div className="tw-px-3 tw-py-1.5 tw-text-xs tw-text-slate-400">
                {T('searching', 'Wyszukiwanie...')}
              </div>
            )}
            {!isLoading && items.length === 0 && (
              <div className="tw-px-3 tw-py-1.5 tw-text-xs tw-text-slate-400">
                {T('noResults', 'Brak wyników')}
              </div>
            )}
            {items.map((it) => (
              <button
                key={it.id}
                type="button"
                onClick={() => {
                  onChange(it.id)
                  setOpen(false)
                  setSearch('')
                }}
                className="tw-block tw-w-full tw-px-3 tw-py-1.5 tw-text-left tw-text-sm hover:tw-bg-slate-100"
              >
                {it.label}
              </button>
            ))}
          </div>,
          document.body,
        )}
    </div>
  )
}
